import React from 'react';
import { parseISO } from 'date-fns';
import { Cruise } from '../context/CruiseContext';
import CustomCruiseIcon from './CustomCruiseIcon';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { Calendar, MapPin, Globe } from 'lucide-react';

interface ShareCountdownImageStoriesProps {
  cruise: Cruise;
  daysUntilDeparture: number;
  shipImageUrl?: string | null;
  itineraryTitle?: string | null;
}

const ShareCountdownImageStories = React.forwardRef<HTMLDivElement, ShareCountdownImageStoriesProps>(
  ({ cruise, daysUntilDeparture, shipImageUrl, itineraryTitle }, ref) => {
    const { formatDate } = useDisplayPreferences();
    
    return (
      <div
        ref={ref}
        data-share-image="true"
        className="relative mx-auto"
        style={{
          width: '1080px',
          height: '1920px',
          fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#070e1b'
        }}
      >
        {/* Background Ship Image - Untinted, bright and clear */}
        {shipImageUrl ? (
          <div className="absolute inset-0">
            <img
              src={shipImageUrl}
              alt=""
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
              style={{ opacity: 1 }}
            />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950 via-blue-900 to-indigo-950" />
        )}

        {/* Soft Vignette Edge Fade on the Border */}
        <div 
          className="absolute inset-0 pointer-events-none z-20"
          style={{
            boxShadow: 'inset 0 0 80px 25px rgba(5, 12, 24, 0.7)',
            boxSizing: 'border-box'
          }}
        />

        {/* 1) Top Compact Glass Card: Countdown & Itinerary (not full width) */}
        <div
          style={{
            position: 'absolute',
            top: '48px',
            left: '0',
            right: '0',
            margin: '0 auto',
            width: 'fit-content',
            minWidth: '520px',
            maxWidth: '820px',
            background: 'rgba(10, 25, 47, 0.78)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            borderRadius: '32px',
            padding: '32px 52px',
            textAlign: 'center',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            boxSizing: 'border-box',
            zIndex: 10
          }}
        >
          {/* Itinerary Title Badge (if present, without AI gem) */}
          {itineraryTitle && (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 24px',
                  borderRadius: '9999px',
                  background: 'rgba(20, 184, 166, 0.25)',
                  border: '1px solid rgba(45, 212, 191, 0.5)',
                  color: '#5eead4',
                  fontSize: '26px',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  boxShadow: '0 4px 14px rgba(20, 184, 166, 0.2)',
                  maxWidth: '100%'
                }}
              >
                <span className="truncate max-w-[700px]">{itineraryTitle}</span>
              </div>
            </div>
          )}

          {/* Countdown Numbers */}
          <div
            style={{
              fontSize: '144px',
              fontWeight: 900,
              lineHeight: 0.9,
              color: '#ffffff',
              letterSpacing: '-0.04em',
              textShadow: '0 8px 28px rgba(0, 0, 0, 0.6)'
            }}
          >
            {daysUntilDeparture}
          </div>

          <div
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#93c5fd',
              marginTop: '12px',
              letterSpacing: '0.02em',
              textTransform: 'lowercase'
            }}
          >
            {daysUntilDeparture === 1 ? 'day' : 'days'} until departure
          </div>
        </div>

        {/* 2) Ship, Departure Date & Port Info Card - Moved down closer to bottom advertisement */}
        <div
          style={{
            position: 'absolute',
            bottom: '232px',
            left: '44px',
            right: '44px',
            background: 'rgba(10, 25, 47, 0.78)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            borderRadius: '32px',
            padding: '28px 36px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            boxSizing: 'border-box',
            zIndex: 10
          }}
        >
          {/* Ship Name & Cruise Line */}
          <div
            style={{
              fontSize: '48px',
              fontWeight: 900,
              color: '#ffffff',
              lineHeight: 1.15,
              textShadow: '0 3px 12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {cruise.shipName}
          </div>
          <div
            style={{
              fontSize: '28px',
              fontWeight: 600,
              color: '#60a5fa',
              marginTop: '6px'
            }}
          >
            {cruise.cruiseLine}
          </div>

          {/* Departure & Port Chips */}
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginTop: '20px',
              flexWrap: 'wrap'
            }}
          >
            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 22px',
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '24px',
                fontWeight: 600
              }}
            >
              <Calendar className="w-6 h-6 text-sky-400 shrink-0" />
              <span>Departing {formatDate(parseISO(cruise.departureDate), { includeWeekday: true, fullMonth: true })}</span>
            </div>

            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 22px',
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '24px',
                fontWeight: 600
              }}
            >
              <MapPin className="w-6 h-6 text-emerald-400 shrink-0" />
              <span>From {cruise.departurePort}</span>
            </div>
          </div>
        </div>

        {/* 3) Bottom Glass Card: Cruising Along (Left) & Available on 3 Platforms (Right) */}
        <div
          style={{
            position: 'absolute',
            bottom: '44px',
            left: '44px',
            right: '44px',
            background: 'rgba(10, 25, 47, 0.78)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            borderRadius: '32px',
            padding: '24px 34px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
            zIndex: 10
          }}
        >
          {/* Left: Logo, Cruising Along Title, and Tagline */}
          <div className="flex items-center gap-4">
            <div 
              style={{
                padding: '14px',
                borderRadius: '22px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <CustomCruiseIcon size={68} className="text-accent" />
            </div>

            <div>
              <div
                style={{
                  fontSize: '42px',
                  fontWeight: 900,
                  color: '#ffffff',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em'
                }}
              >
                Cruising Along
              </div>
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: '#93c5fd',
                  marginTop: '4px'
                }}
              >
                Track Your Cruise Adventures
              </div>
            </div>
          </div>

          {/* Right: Available on: with App Stores and URL on two separate rows */}
          <div 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '8px'
            }}
          >
            <span 
              style={{ 
                fontSize: '18px', 
                fontWeight: 700, 
                color: 'rgba(255, 255, 255, 0.85)', 
                letterSpacing: '0.06em',
                textTransform: 'uppercase' 
              }}
            >
              Available on:
            </span>

            {/* Row 1: App Store & Google Play */}
            <div className="flex items-center gap-2.5">
              {/* Apple App Store badge */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 16px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  fontSize: '19px',
                  fontWeight: 600
                }}
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 384 512">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/>
                </svg>
                <span>App Store</span>
              </div>

              {/* Google Play badge */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 16px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  fontSize: '19px',
                  fontWeight: 600
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <path d="M3.609 1.814L13.793 12 3.61 22.186A2.215 2.215 0 0 1 3 20.615V3.385c0-.608.225-1.168.609-1.571z" fill="#00D3FF"/>
                  <path d="M17.207 8.586L13.793 12l3.414 3.414 4.093-2.34a1.233 1.233 0 0 0 0-2.148l-4.093-2.34z" fill="#FFCE00"/>
                  <path d="M13.793 12L3.609 1.814c.371-.389.914-.614 1.516-.614.475 0 .949.141 1.359.375l10.723 6.125L13.793 12z" fill="#00E676"/>
                  <path d="M13.793 12l3.414 3.414L5.125 21.539c-.41.234-.884.375-1.359.375-.602 0-1.145-.225-1.516-.614L13.793 12z" fill="#FF334B"/>
                </svg>
                <span>Google Play</span>
              </div>
            </div>

            {/* Row 2: Website URL */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '7px 16px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                color: '#93c5fd',
                fontSize: '19px',
                fontWeight: 600
              }}
            >
              <Globe className="w-5 h-5 text-sky-400 shrink-0" />
              <span>keep.cruisingalong.com</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ShareCountdownImageStories.displayName = 'ShareCountdownImageStories';

export default ShareCountdownImageStories;
